/**
 * @file TabOptin.jsx
 * @description TabOptin component for displaying a customer's opt-outs.
 *              Detection mirrors the dashboard /getoptin route: a customer is
 *              opted out once they reply STOP. Only opt-OUT rows come back, so
 *              there's no status column — every row is an opt-out by definition.
 * @component TabOptin
 * @exports TabOptin - Main component for displaying customer opt-outs.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from "@mui/material";
import { getcustomeroptin } from "../../../services/customerinfoModule/historyServices";
import { useRegisterCsvExport } from "../../customerModule/customers/CsvExportContext.jsx";
import { OPTOUT_COLUMNS } from "../../customerModule/customers/customerExportConfig.js";

const CustomerOptIn = ({ customerguid }) => {
  const [optOutData, setOptOutData] = useState([]);
  const [loading, setLoading] = useState(false);

  useRegisterCsvExport({
    name: "optout",
    columns: OPTOUT_COLUMNS,
    rows: optOutData,
    enabled: !loading,
  });

  useEffect(() => {
    if (customerguid) {
      fetchOptOutDetails();
    }
  }, [customerguid]);

  const fetchOptOutDetails = async () => {
    setLoading(true);
    try {
      const data = await getcustomeroptin({ customerguid });
      setOptOutData(data || []);
    } catch (error) {
      console.error("Error fetching opt-out data:", error);
      setOptOutData([]);
    } finally {
      setLoading(false);
    }
  };

  // MM/dd/yyyy hh:mm AM/PM
  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${mm}/${dd}/${yyyy} ${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}><b>Channel</b></TableCell>
              <TableCell><b>Contact</b></TableCell>
              <TableCell sx={{ width: 190, whiteSpace: "nowrap" }}><b>Opt-Out Date</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : optOutData.length > 0 ? (
              optOutData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.Channel}</TableCell>
                  <TableCell>{row.ContactValue || "—"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(row.Date)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No opt-outs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerOptIn;